import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { apiGet } from "../services/api";
import { useAuth } from "./AuthContext";

export interface PrintTemplateConfig {
  showHeaderShopName: boolean;
  showHeaderAddress: boolean;
  showHeaderPostal: boolean;
  showHeaderContact: boolean;
  showHeaderEmail: boolean;
  showHeaderKraPin: boolean;
  headerFieldsOrder: string[];
  headerAlignment: "left" | "center" | "right";
  showFooterText: boolean;
  footerCustomText: string;
  showFooterShopName?: boolean;
  showFooterAddress?: boolean;
  showFooterPostal?: boolean;
  showFooterContact?: boolean;
  showFooterEmail?: boolean;
  showFooterKraPin?: boolean;
  footerFieldsOrder: string[];
}

export interface ShopSettings {
  shopName: string;
  logoUrl: string | null;
  address: string;
  vatRegistrationNumber: string;
  kraPin: string;
  currency: string;
  invoicePrefix: string;
  receiptPrefix: string;
  invoiceFooterText: string;
  defaultInvoiceFormat: "a4" | "thermal";
  defaultVatMode: "inclusive" | "exclusive";
  emailNotificationsEnabled: boolean;
  notificationEmail: string | null;
  invoiceCounter: number;
  receiptCounter: number;
  updatedAt?: unknown;
  printTemplates?: {
    thermal: PrintTemplateConfig;
    a4: PrintTemplateConfig;
  };
  phone?: string;
  email?: string;
  postalAddress?: string;
  subscriptionTier?: string;
  enabledModules?: string[];
  positions?: string[];
}

export interface SettingsContextValue {
  shopName: string;
  logoUrl: string | null;
  address: string;
  vatRegistrationNumber: string;
  kraPin: string;
  currency: string;
  invoicePrefix: string;
  receiptPrefix: string;
  invoiceFooterText: string;
  defaultInvoiceFormat: "a4" | "thermal";
  defaultVatMode: "inclusive" | "exclusive";
  emailNotificationsEnabled: boolean;
  notificationEmail: string | null;
  isLoading: boolean;
  printTemplates: {
    thermal: PrintTemplateConfig;
    a4: PrintTemplateConfig;
  };
  phone: string;
  email: string;
  postalAddress: string;
  subscriptionTier: string;
  enabledModules: string[];
  positions: string[];
  refreshSettings: () => Promise<void>;
}

const DEFAULT_THERMAL_TEMPLATE: PrintTemplateConfig = {
  showHeaderShopName: true,
  showHeaderAddress: false,
  showHeaderPostal: true,
  showHeaderContact: true,
  showHeaderEmail: true,
  showHeaderKraPin: true,
  headerFieldsOrder: ["shopName", "address", "postal", "contact", "email", "kraPin"],
  headerAlignment: "center",
  showFooterText: true,
  footerCustomText: "Thank you for your business!",
  showFooterShopName: false,
  showFooterAddress: false,
  showFooterPostal: false,
  showFooterContact: false,
  showFooterEmail: false,
  showFooterKraPin: false,
  footerFieldsOrder: ["shopName", "address", "postal", "contact", "email", "kraPin"],
};

const DEFAULT_A4_TEMPLATE: PrintTemplateConfig = {
  showHeaderShopName: true,
  showHeaderAddress: true,
  showHeaderPostal: true,
  showHeaderContact: true,
  showHeaderEmail: true,
  showHeaderKraPin: true,
  headerFieldsOrder: ["shopName", "address", "postal", "contact", "email", "kraPin"],
  headerAlignment: "right",
  showFooterText: true,
  footerCustomText: "Thank you for your business!",
  showFooterShopName: false,
  showFooterAddress: false,
  showFooterPostal: false,
  showFooterContact: false,
  showFooterEmail: false,
  showFooterKraPin: false,
  footerFieldsOrder: ["shopName", "address", "postal", "contact", "email", "kraPin"],
};

const DEFAULT_SETTINGS: Omit<SettingsContextValue, "isLoading" | "refreshSettings"> = {
  shopName: "Shop",
  logoUrl: null,
  address: "",
  vatRegistrationNumber: "",
  kraPin: "",
  currency: "USD",
  invoicePrefix: "INV-",
  receiptPrefix: "RCP-",
  invoiceFooterText: "",
  defaultInvoiceFormat: "a4",
  defaultVatMode: "inclusive",
  emailNotificationsEnabled: false,
  notificationEmail: null,
  printTemplates: {
    thermal: DEFAULT_THERMAL_TEMPLATE,
    a4: DEFAULT_A4_TEMPLATE,
  },
  phone: "",
  email: "",
  postalAddress: "",
  subscriptionTier: "starter",
  enabledModules: ["inventory", "sales"],
  positions: [
    "Manager",
    "Sales Representative",
    "Cashier",
    "Accountant",
    "Storekeeper",
    "HR Specialist",
    "Developer",
    "Delivery Driver",
  ],
};

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { user, role } = useAuth();
  const [settings, setSettings] =
    useState<Omit<SettingsContextValue, "isLoading" | "refreshSettings">>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  const loadSettings = async (cancelled = false) => {
    setIsLoading(true);
    try {
      const res = await apiGet<{ settings?: ShopSettings; data?: ShopSettings } | ShopSettings>("/api/settings/shop");
      const data = (res as { settings?: ShopSettings }).settings || (res as { data?: ShopSettings }).data || (res as ShopSettings);
      if (!cancelled && data) {
        setSettings({
          shopName: data.shopName ?? DEFAULT_SETTINGS.shopName,
          logoUrl: data.logoUrl ?? DEFAULT_SETTINGS.logoUrl,
          address: data.address ?? DEFAULT_SETTINGS.address,
          vatRegistrationNumber:
            data.vatRegistrationNumber ??
            DEFAULT_SETTINGS.vatRegistrationNumber,
          kraPin: data.kraPin ?? DEFAULT_SETTINGS.kraPin,
          currency: data.currency ?? DEFAULT_SETTINGS.currency,
          invoicePrefix:
            data.invoicePrefix ?? DEFAULT_SETTINGS.invoicePrefix,
          receiptPrefix:
            data.receiptPrefix ?? DEFAULT_SETTINGS.receiptPrefix,
          invoiceFooterText:
            data.invoiceFooterText ?? DEFAULT_SETTINGS.invoiceFooterText,
          defaultInvoiceFormat:
            data.defaultInvoiceFormat ??
            DEFAULT_SETTINGS.defaultInvoiceFormat,
          defaultVatMode:
            data.defaultVatMode ?? DEFAULT_SETTINGS.defaultVatMode,
          emailNotificationsEnabled:
            data.emailNotificationsEnabled ??
            DEFAULT_SETTINGS.emailNotificationsEnabled,
          notificationEmail:
            data.notificationEmail ?? DEFAULT_SETTINGS.notificationEmail,
          printTemplates: {
            thermal: {
              ...DEFAULT_THERMAL_TEMPLATE,
              ...(data.printTemplates?.thermal ?? {}),
            },
            a4: {
              ...DEFAULT_A4_TEMPLATE,
              ...(data.printTemplates?.a4 ?? {}),
            },
          },
          phone: data.phone ?? DEFAULT_SETTINGS.phone,
          email: data.email ?? DEFAULT_SETTINGS.email,
          postalAddress: data.postalAddress ?? DEFAULT_SETTINGS.postalAddress,
          subscriptionTier: (data as any).subscriptionTier ?? DEFAULT_SETTINGS.subscriptionTier,
          enabledModules: (data as any).enabledModules ?? DEFAULT_SETTINGS.enabledModules,
          positions: data.positions ?? DEFAULT_SETTINGS.positions,
        });
      }
    } catch {
      if (!cancelled) setSettings(DEFAULT_SETTINGS);
    } finally {
      if (!cancelled) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user || role === ("super_admin" as any)) {
      setSettings(DEFAULT_SETTINGS);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    loadSettings(cancelled);
    return () => { cancelled = true; };
  }, [user]);

  const refreshSettings = async () => {
    await loadSettings(false);
  };

  return (
    <SettingsContext.Provider value={{ ...settings, isLoading, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return ctx;
}
